/**
 * Un jour calendaire, ou rien.
 *
 * Deux vues du CBS affichent une date de valeur, et toutes deux tombaient dans
 * les memes pieges (#169) :
 *
 * - `value_date` est absent tant qu'aucun reglement n'a eu lieu — nul dans le
 *   grand livre, chaine vide dans la revue d'un paiement — et `new Date(null)`
 *   vaut l'epoch quand `new Date('')` est invalide. D'ou 01/01/1970 d'un cote
 *   et « Invalid Date » de l'autre.
 * - c'est une colonne `date`, servie a minuit UTC : la lire en heure locale
 *   rendrait la veille a l'ouest de Greenwich, d'ou les parties prises une a
 *   une.
 * - `new Date(2026, 1, 30)` ne se plaint pas, il rend le 2 mars. Un jour qui
 *   n'existe pas se lirait donc comme un autre jour, sans que rien ne le
 *   signale — et un horodatage impossible, comme une heure 25, se lirait par
 *   son seul prefixe.
 *
 * Rend `null` dans tous ces cas : a l'appelant de dire ce qu'il affiche alors.
 */
export function parseCalendarDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;

  // La partie jour doit couvrir toute la chaine, ou etre suivie d'un
  // horodatage que `Date` accepte. En se contentant du prefixe, on lisait
  // `2026-04-01T25:00:00Z` comme le 1er avril : une entree incomprise passait
  // pour une date valide.
  const parts = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(raw);
  if (!parts) {
    const instant = new Date(raw);
    return Number.isNaN(instant.getTime()) ? null : instant;
  }
  if (raw.length > 10 && Number.isNaN(new Date(raw).getTime())) {
    return null;
  }

  const year = Number(parts[1]);
  const month = Number(parts[2]) - 1;
  const day = Number(parts[3]);
  const date = new Date(year, month, day);

  // Le 30 fevrier deviendrait le 2 mars sans bruit.
  const roundTrips =
    date.getFullYear() === year && date.getMonth() === month && date.getDate() === day;
  return roundTrips ? date : null;
}

/**
 * Aujourd'hui, au calendrier de celui qui regarde l'ecran.
 *
 * `toISOString().slice(0, 10)` rend le jour UTC : a Luxembourg, un operateur
 * qui ouvre un formulaire entre minuit et une ou deux heures du matin se
 * verrait proposer la veille, et la bookerait telle quelle sans rien
 * remarquer.
 */
export function todayCalendarDate(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
